import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import posthog from 'posthog-js';
import { phrasesApi, practiceApi, analyzeAudioApi, settingsApi, collectionsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { MASTER_VOLUME, BEEP_GAIN_RATIO } from '../lib/audioLevel';
import { useWakeLock } from '../lib/useWakeLock';
import { reportError, feedbackShownProps, requestFailureReason } from '../lib/analytics';
import { speak, warmUpSpeech, AudioRecorder, MIC_CONSTRAINTS_EARLY, newRecDiag, MAX_RECORDING_MS, RECORDING_CAPPED_MESSAGE, loadVoices, getSavedSpeed, saveSpeed, getSavedPlaybacks, getSavedKaraoke, getSavedPlayEnglish, useVoiceCapabilities, waitForSilence, measureLoudness, IS_IOS, playRecording, PLAYBACK_FAILED_MESSAGE } from '../lib/speech';
import { trackPlayback } from '../lib/practiceTime';
import { speakFrench, cancelFrench, getSavedTTSVoice, unlockAudioPlayback, getUnlockedPlayer } from '../lib/tts';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SessionHeader from '../components/SessionHeader';
import PhraseDisplay from '../components/PhraseDisplay';
import PhraseCard from '../components/PhraseCard';
import SpeedControl from '../components/SpeedControl';
import { useSwipe } from '../lib/useSwipe';
// Only words with a tip card below them are tinted, the same rule the Manual
// result and Quick Practice follow.
import { feedbackKeys, occKey } from '../lib/score';
import ScoreHeader from '../components/ScoreHeader';
import SessionSummary from '../components/SessionSummary';
import { UpgradeDialog } from '../components/ProUpgrade';
import NextPhraseButton from '../components/NextPhraseButton';
import TryAgainButton from '../components/TryAgainButton';
import RecordingNotice, { scoreFlashNotice } from '../components/RecordingNotice';
import { ScoredPhrase, PronunciationTips, PickedWordTips, TapWordPrompt } from '../components/PronunciationTips';
import FeedbackBox from '../components/FeedbackBox';
import { pronunciationBeta, REDO_BELOW } from '../lib/score';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CollectionOptions } from '../components/CollectionSelect';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { markPractised } from '../lib/installPrompt';
import { ArrowLeft, Volume2, Headphones, Repeat, Mic, Target, Sparkles, Loader2, PlayCircle, Pause, Play, Folder, LogOut, X } from 'lucide-react';

const PHASE_LABELS = {
    playing: 'Listening...',
    gap: 'Your turn, repeat now...',
    countdown: 'Get ready...',
    recording: 'Recording...',
    analyzing: 'Analyzing...',
    score: '',
    idle: '',
};

const Automatic = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    // Desktop only: the mic is opened once for the whole session and lent to
    // each recording (AudioRecorder.useStream). See useStream for why.
    const sessionMicRef = useRef(null);
    const closeSessionMic = () => {
        const s = sessionMicRef.current;
        sessionMicRef.current = null;
        if (s) s.getTracks().forEach(t => t.stop());
    };
    const [searchParams] = useSearchParams();
    const collectionIds = searchParams.get('collections')?.split(',').filter(Boolean) || [];
    const phraseIds = searchParams.get('phrase_ids')?.split(',').filter(Boolean) || []; // specific set (e.g. weakest phrases)
    const order = searchParams.get('order') || 'smart';
    const singlePhraseId = searchParams.get('phrase_id') || null;
    // Quick Practice (single phrase) always scores so it can show a results card.
    const mode = singlePhraseId ? 'listen-record' : (searchParams.get('mode') || 'listen-repeat');
    const returnTo = searchParams.get('returnTo') === 'collections' ? '/collections' : '/dashboard';

    const [collectionLabel, setCollectionLabel] = useState('');
    // Where the picked-word controls get portalled to: below both
    // phrases, so opening them cannot shove either one down the page.
    const [wordFooter, setWordFooter] = useState(null);
    const [collections, setCollections] = useState([]);
    const [phrases, setPhrases] = useState([]);
    // Working list the loop actually iterates (kept in a ref so a "redo" run can swap
    // it synchronously); allPhrasesRef holds the full original set for a full restart.
    const phrasesRef = useRef([]);
    const allPhrasesRef = useRef([]);
    // Per-phrase scores captured during a scored session, shown as a summary at the end.
    const sessionResultsRef = useRef([]);
    const [sessionSummary, setSessionSummary] = useState(null);
    // Stats-only summary for non-scoring modes (Listen & Repeat / Listen Only).
    const [statsSummary, setStatsSummary] = useState(null);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isActive, setIsActive] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    // The highlighted word tapped on the score view (beta). Cleared with every
    // new score, so a card never outlives its phrase.
    const [pickedWord, setPickedWord] = useState(null);
    const [phase, setPhase] = useState('idle');
    const [lastScore, setLastScore] = useState(null);
    useEffect(() => { setPickedWord(null); }, [lastScore]);
    // Quick Practice single-phrase result, shown on a Manual-style results card.
    const [singleResult, setSingleResult] = useState(null);
    const [singleRecordingUrl, setSingleRecordingUrl] = useState(null);
    // The bytes behind that url, for playing one word back.
    const [singleRecordingBlob, setSingleRecordingBlob] = useState(null);
    // The server's 402 detail while the upgrade dialog is open, null otherwise.
    const [upgradePrompt, setUpgradePrompt] = useState(null);
    const [isPlayingSingle, setIsPlayingSingle] = useState(false);
    const [isPlayingTarget, setIsPlayingTarget] = useState(false);
    const singlePlayerRef = useRef(null);
    const [playbackSpeed, setPlaybackSpeed] = useState(getSavedSpeed);
    const [sessionStats, setSessionStats] = useState({ practiced: 0, totalScore: 0 });

    const [activeCharIndex, setActiveCharIndex] = useState(-1);
    const [karaokeEnabled] = useState(getSavedKaraoke);
    const { caps: voiceCaps, ready: capsReady } = useVoiceCapabilities();

    const stoppedRef = useRef(false);
    const timerRef = useRef(null);
    // When set (during the score-display phase), calling it jumps to the next
    // phrase early. Lets learners skip the score linger when they've already
    // read the feedback, while the auto-timer still catches them if they don't.
    const scoreSkipRef = useRef(null);
    // Same idea one phase earlier: set while the learner's own recording is
    // playing back, so they can cut their voice short and go straight to the
    // feedback instead of sitting through a clip they've already heard.
    const playbackSkipRef = useRef(null);
    // Tracks how far the CURRENT phrase's sub-phases have progressed so that a
    // pause+resume picks up where it left off instead of restarting the whole
    // English + French-listen + countdown sequence. Reset only when a genuinely
    // new phrase begins — carried across a resume of the same phrase.
    const phraseProgressRef = useRef({ englishDone: false, listenDone: false, countdownDone: false });
    // True only when handlePause caught a phrase mid-flow (before its score
    // was recorded). Read by handleResume to decide whether to preserve
    // phraseProgressRef — advancing to the next phrase must start fresh.
    const pausedMidPhraseRef = useRef(false);
    // Set by handleResume so the loop's first iteration knows NOT to wipe
    // phraseProgressRef — that iteration is a continuation of the paused phrase.
    const isResumingRef = useRef(false);
    // Bumped by every runSession. Going back a phrase starts a new loop while
    // the old one may still be waiting on audio; the old one sees its number is
    // stale and stops, instead of running on alongside the new one.
    const runIdRef = useRef(0);
    const recognizerRef = useRef(null);
    const audioRecorderRef = useRef(null);
    const audioPlayerRef = useRef(null);
    // Persistent element unlocked during the iOS Start tap, reused for playback.
    // Persistent AudioContext for the beep, created + resumed during the Start/Resume
    // tap so iOS keeps it running. A fresh context created mid-loop starts suspended,
    // and in listen-only (no mic to keep the audio session alive) resume() can hang
    // forever — which stalled the loop after the first phrase. See unlockAudioContext.
    const audioCtxRef = useRef(null);
    const resumeIndexRef = useRef(0);
    const phraseCompletedRef = useRef(false);
    const wordBoundariesRef = useRef([]);   // [{charIndex, elapsed}] from last TTS
    const highlightTimersRef = useRef([]);  // setTimeout handles for replayed highlighting
    const sessionDurationRef = useRef(0);  // accumulated active ms (excludes paused time)
    const segmentStartRef = useRef(null);  // wall-clock ms when current segment started
    const recordingDurationRef = useRef(0);
    const recStartRef = useRef(0);         // wall-clock ms when recording actually began
    const englishWarnedRef = useRef(false); // warn once if no English voice is installed
    const countdownEnabledRef = useRef(true);

    const [englishSpeaking, setEnglishSpeaking] = useState(false);
    const [recordingProgress, setRecordingProgress] = useState(0);
    const [playbackProgress, setPlaybackProgress] = useState(0);
    const [scoreProgress, setScoreProgress] = useState(0);
    const [countdownValue, setCountdownValue] = useState(null);
    const progressIntervalRef = useRef(null);
    const playbackDurationRef = useRef(0);
    const scoreDurationRef = useRef(0);

    // Keep the screen on for the whole session so the OS screensaver/auto-lock
    // doesn't interrupt mid-phrase.
    useWakeLock(true);

    useEffect(() => {
        fetchPhrases();
        if (!singlePhraseId) {
            if (collectionIds.length === 0) {
                setCollectionLabel('All Phrases');
            } else {
                collectionsApi.getAll().then(res => {
                    const names = collectionIds.map(id => res.data.find(c => c.id === id)?.name).filter(Boolean);
                    setCollectionLabel(names.length ? names.join(', ') : 'All Phrases');
                }).catch(() => {});
            }
        }
        collectionsApi.getAll().then(res => setCollections(res.data)).catch(() => {});
        settingsApi.get().then(res => {
            countdownEnabledRef.current = res.data.countdown_enabled ?? true;
        }).catch(() => {});
        return () => {
            stoppedRef.current = true;
            cleanup();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-start the session once phrases are loaded so Automatic launches straight
    // into the loop (matching Manual), instead of stopping on a separate preview page.
    // NOT on iOS: WebKit silently drops the first speechSynthesis.speak() unless it
    // comes from a user gesture, so an auto-started session plays no TTS. On iOS we
    // leave the idle screen up and let the existing "Start Session" button kick it off
    // — the same gesture -> runSession -> speak path as Resume, which works.
    const autoStartedRef = useRef(false);
    useEffect(() => {
        if (autoStartedRef.current) return;
        if (loading || phrases.length === 0) return;
        if (IS_IOS) return;
        autoStartedRef.current = true;
        handleStart();
    }, [loading, phrases]); // eslint-disable-line react-hooks/exhaustive-deps

    // Old iOS/Safari with no MediaRecorder can't record at all — track the drop-off.
    useEffect(() => {
        if (typeof MediaRecorder === 'undefined') {
            reportError('mic_unsupported',{ error_type: 'media_recorder_unsupported', mode: 'automatic' });
        }
    }, []);

    useEffect(() => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        if (phase === 'recording' && recordingDurationRef.current > 0) {
            setRecordingProgress(0);
            const start = Date.now();
            const duration = recordingDurationRef.current;
            progressIntervalRef.current = setInterval(() => {
                const pct = Math.min(((Date.now() - start) / duration) * 100, 100);
                setRecordingProgress(pct);
                if (pct >= 100) clearInterval(progressIntervalRef.current);
            }, 50);
        } else if (phase === 'playback') {
            // The playback bar is started from startPlaybackProgress() when the
            // audio actually begins (onplaying), not here — otherwise on Android
            // the bar would run during playback start-up latency and finish before
            // the voice is audible. Just reset it to empty until then.
            setPlaybackProgress(0);
        } else if (phase === 'score' && scoreDurationRef.current > 0) {
            setScoreProgress(0);
            const start = Date.now();
            const duration = scoreDurationRef.current;
            progressIntervalRef.current = setInterval(() => {
                const pct = Math.min(((Date.now() - start) / duration) * 100, 100);
                setScoreProgress(pct);
                if (pct >= 100) clearInterval(progressIntervalRef.current);
            }, 50);
        } else {
            setRecordingProgress(0);
            setPlaybackProgress(0);
            setScoreProgress(0);
        }
        return () => { if (progressIntervalRef.current) clearInterval(progressIntervalRef.current); };
    }, [phase]);

    // Start the playback progress line. Called when the recording audio actually
    // begins (onplaying) so the line stays in lockstep with the voice, even when
    // Android adds start-up latency before playback.
    const startPlaybackProgress = (durationMs) => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        setPlaybackProgress(0);
        const start = Date.now();
        const duration = durationMs > 0 ? durationMs : 1500;
        progressIntervalRef.current = setInterval(() => {
            const pct = Math.min(((Date.now() - start) / duration) * 100, 100);
            setPlaybackProgress(pct);
            if (pct >= 100) clearInterval(progressIntervalRef.current);
        }, 50);
    };

    const clearHighlightTimers = () => {
        highlightTimersRef.current.forEach(t => clearTimeout(t));
        highlightTimersRef.current = [];
    };

    const replayHighlighting = (boundaries) => {
        clearHighlightTimers();
        setActiveCharIndex(-1);
        // Captured timings include the TTS engine's spin-up latency before the first
        // word, so rebase to the first word's time — the replay then starts immediately
        // (right after the beep) instead of waiting out that leading gap.
        const offset = boundaries.length ? boundaries[0].elapsed : 0;
        boundaries = boundaries.map(b => ({ charIndex: b.charIndex, elapsed: Math.max(0, b.elapsed - offset) }));
        boundaries.forEach(({ charIndex, elapsed }) => {
            const t = setTimeout(() => setActiveCharIndex(charIndex), elapsed);
            highlightTimersRef.current.push(t);
        });
    };

    const cleanup = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        clearHighlightTimers();
        if (recognizerRef.current) recognizerRef.current.stop();
        if (audioRecorderRef.current) audioRecorderRef.current.cleanup();
        closeSessionMic();
        if (audioPlayerRef.current) { audioPlayerRef.current.pause(); audioPlayerRef.current = null; }
        if (singlePlayerRef.current) { singlePlayerRef.current.pause(); singlePlayerRef.current = null; }
        if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
        window.speechSynthesis.cancel();
        cancelFrench();
        setActiveCharIndex(-1);
    };

    const fetchPhrases = async () => {
        try {
            // Single-phrase automatic run (from Quick Practice): load just that phrase.
            if (singlePhraseId) {
                const res = await phrasesApi.getById(singlePhraseId);
                setPhrases([res.data]);
                phrasesRef.current = [res.data];
                allPhrasesRef.current = [res.data];
                const cids = res.data.collection_ids || [];
                if (cids.length > 0) {
                    collectionsApi.getAll().then(cr => {
                        const name = cr.data.find(c => cids.includes(c.id))?.name;
                        if (name) setCollectionLabel(name);
                    }).catch(() => {});
                }
                return;
            }
            // Specific set of phrases in the given order (e.g. "practise weakest").
            if (phraseIds.length > 0) {
                const res = await phrasesApi.getAll();
                const byId = new Map(res.data.map(p => [p.id, p]));
                const ordered = phraseIds.map(id => byId.get(id)).filter(Boolean);
                if (ordered.length > 0) {
                    setPhrases(ordered);
                    phrasesRef.current = ordered;
                    allPhrasesRef.current = ordered;
                    setCollectionLabel(searchParams.get('label') || 'Your weakest phrases');
                    return;
                }
            }
            const promises = collectionIds.length > 0
                ? collectionIds.map(id => phrasesApi.getAll(id))
                : [phrasesApi.getAll()];
            const results = await Promise.all(promises);
            const allPhrases = results.flatMap(r => r.data);
            let unique = [...new Map(allPhrases.map(p => [p.id, p])).values()];
            if (unique.length === 0) {
                toast.error('No phrases found.');
                navigate('/dashboard');
                return;
            }
            if (order === 'random') {
                for (let i = unique.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [unique[i], unique[j]] = [unique[j], unique[i]];
                }
            } else if (order === 'added') {
                unique.sort((a, b) => (a.created_at || '') < (b.created_at || '') ? -1 : (a.created_at || '') > (b.created_at || '') ? 1 : 0);
            } else {
                unique.sort((a, b) => {
                    const aNever = (a.practice_count || 0) === 0;
                    const bNever = (b.practice_count || 0) === 0;
                    if (aNever !== bNever) return aNever ? -1 : 1;
                    const scoreDiff = (a.average_score || 0) - (b.average_score || 0);
                    if (scoreDiff !== 0) return scoreDiff;
                    const aLast = a.last_practiced || '';
                    const bLast = b.last_practiced || '';
                    return aLast < bLast ? -1 : aLast > bLast ? 1 : 0;
                });
            }
            setPhrases(unique);
            phrasesRef.current = unique;
            allPhrasesRef.current = unique;
        } catch {
            toast.error('Failed to load phrases');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const wait = (ms) => new Promise(resolve => {
        timerRef.current = setTimeout(resolve, ms);
    });

    // Get (or lazily create) the shared beep AudioContext. Created + resumed inside
    // the Start/Resume user gesture so iOS keeps it in the 'running' state.
    const unlockAudioContext = () => {
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            if (audioCtxRef.current.state === 'suspended') {
                audioCtxRef.current.resume().catch(() => {});
            }
        } catch { /* AudioContext unavailable */ }
        return audioCtxRef.current;
    };

    const playBeep = () => new Promise((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        // Belt-and-braces: never let a suspended/blocked AudioContext hang the loop.
        // iOS suspends contexts and resume() can stay pending forever when no mic keeps
        // the audio session alive (listen-only) — that stalled the loop after phrase 1.
        // If nothing has happened by 700ms, resolve so the loop always advances; a beep
        // that's silent or slightly late is harmless, a hang is not.
        const guard = setTimeout(finish, 700);
        const settle = () => { clearTimeout(guard); finish(); };
        try {
            const ctx = audioCtxRef.current || unlockAudioContext();
            if (!ctx) { settle(); return; }
            const play = () => {
                try {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.frequency.value = 880;
                    // Attack -> hold -> release. Holding at peak for most of the duration
                    // reads much louder than an immediate exponential decay (which made the
                    // beep a faint tick, especially on iOS) — closer to the sustained TTS.
                    const t0 = ctx.currentTime;
                    const dur = 0.16;
                    const peak = MASTER_VOLUME * BEEP_GAIN_RATIO;
                    gain.gain.setValueAtTime(0.0001, t0);
                    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012); // fast attack (no click)
                    gain.gain.setValueAtTime(peak, t0 + dur - 0.03);          // hold at peak
                    gain.gain.exponentialRampToValueAtTime(0.001, t0 + dur);  // short release
                    osc.start(t0);
                    osc.stop(t0 + dur);
                    osc.onended = settle;
                } catch { settle(); }
            };
            // Reuse the shared context (do NOT close it — it's reused every beep and
            // closed in cleanup). Resume if iOS suspended it, but don't await forever.
            if (ctx.state === 'suspended') {
                ctx.resume().then(play).catch(settle);
            } else {
                play();
            }
        } catch { settle(); }
    });

    const isStopped = () => stoppedRef.current;

    // One summary row per phrase: going back and redoing one replaces its row,
    // the same as Manual's recordCurrentResult.
    const keepResult = (entry) => {
        const idx = sessionResultsRef.current.findIndex(e => e.phrase.id === entry.phrase.id);
        if (idx >= 0) sessionResultsRef.current[idx] = entry;
        else sessionResultsRef.current.push(entry);
    };

    // Swipe the card left to do exactly what the Next arrow does: end the
    // playback of their own voice, or move on from the score screen. On a long
    // result the arrow is off the screen, same as in Manual. It does nothing in
    // the phases with no arrow, where the flow is mid-phrase and moving on
    // would mean abandoning a recording.
    const advanceSwipe = useSwipe({
        onSwipeLeft: () => { if (canGoForward) handleNextArrow(); },
        onSwipeRight: () => { if (canGoBack) handlePrevPhrase(); },
    });

    const playbackRecording = (audioUrl, onStart = null) => new Promise((resolve) => {
        if (!audioUrl) { resolve(); return; }
        // On iOS reuse the element unlocked during the Start tap — a fresh Audio()
        // would be blocked from play()ing here (no user gesture in the loop).
        const audio = getUnlockedPlayer() || new Audio();
        audio.src = audioUrl;
        audioPlayerRef.current = audio;
        let started = false;
        let done = false;
        // Resolve exactly once, whether the clip ended, errored, or the learner
        // tapped the arrow to cut their own playback short.
        const finish = () => {
            if (done) return;
            done = true;
            playbackSkipRef.current = null;
            audioPlayerRef.current = null;
            resolve();
        };
        playbackSkipRef.current = () => {
            try { audio.pause(); } catch { /* silent */ }
            finish();
        };
        // Fire onStart exactly when audio actually begins playing so highlighting
        // stays in sync with the recording instead of running ahead of it.
        audio.onplaying = () => { if (!started) { started = true; if (onStart) onStart(); } };
        audio.onended = finish;
        audio.onerror = finish;
        trackPlayback(audio);   // listening to your own voice is practice too
        const p = audio.play();
        if (p && p.catch) p.catch(finish);
    });

    const runSession = async (startIdx, speed, voice) => {
        const runId = ++runIdRef.current;
        // Shadows the outer one for this loop: stopped, or replaced by a newer run.
        const isStopped = () => stoppedRef.current || runIdRef.current !== runId;
        stoppedRef.current = false;
        setIsActive(true);
        setIsPaused(false);
        setLastScore(null);

        // Desktop, in a mode that records: switch the mic on now, before the
        // first phrase plays, so its start-up silence is over before anyone
        // records. Echo cancellation off (MIC_CONSTRAINTS_EARLY): nothing plays
        // while recording, and it keeps the open mic from changing how the
        // computer plays the model voice. If it fails, each phrase opens its own
        // mic as before.
        // Everyone on desktop since 19 Oct 2026 (tested by Leanne: no gaps, no
        // cut-offs). Not iPhones, and not iPads, which present as a Mac (a
        // touch screen gives them away): an open mic on Apple devices changes
        // how sound plays and could make the model voice quieter all session.
        const ua = navigator.userAgent || '';
        const iPad = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
        const desktop = !iPad && !/Android|iPhone|iPad|iPod/i.test(ua);
        if (desktop && (mode === 'listen-record' || mode === 'listen-repeat')
                && !(sessionMicRef.current && sessionMicRef.current.getAudioTracks().some(t => t.readyState === 'live'))) {
            try {
                sessionMicRef.current = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS_EARLY });
            } catch {
                sessionMicRef.current = null;
            }
        }

        const list = phrasesRef.current;
        for (let i = startIdx; i < list.length; i++) {
            if (isStopped()) break;
            // Preserve the paused phrase's sub-phase progress on the first iteration
            // of a resumed session; wipe it for every genuinely new phrase.
            if (isResumingRef.current && i === startIdx) {
                isResumingRef.current = false;
            } else {
                phraseProgressRef.current = { englishDone: false, listenDone: false, countdownDone: false };
            }
            phraseCompletedRef.current = false;
            resumeIndexRef.current = i;
            const phrase = list[i];
            setCurrentIndex(i);
            const logEvent = () => {};

            // Phase 1: Play TTS (repeated playbacksPerPhrase times)
            logEvent('TTS start');
            setPhase('playing');

            // Optionally read the English translation aloud first, so the learner
            // hears the meaning before the French they will repeat.
            // Skipped when resuming a phrase whose English already played — otherwise
            // pause + resume would replay the whole English + French listen intro.
            if (getSavedPlayEnglish() && phrase.english && !isStopped() && !phraseProgressRef.current.englishDone) {
                // Explicitly pick an English voice (prefer British). If the device
                // has no English voice the engine would otherwise read English with
                // the French voice — so warn the user once instead.
                // Must await loadVoices(): getVoices() is populated ASYNCHRONOUSLY on
                // iOS and Android Chrome, so calling it directly returned [] early in
                // a session and the English was silently skipped on those devices.
                const allVoices = await loadVoices();
                const enVoice =
                    allVoices.find(v => v.lang.toLowerCase().replace('_', '-') === 'en-gb') ||
                    allVoices.find(v => v.lang.toLowerCase().startsWith('en'));
                if (enVoice) {
                    try {
                        // On the very first phrase of a session, pause briefly so the
                        // user registers what's happening before the English starts.
                        if (i === startIdx) await new Promise(res => setTimeout(res, 1200));
                        if (isStopped()) break;
                        setEnglishSpeaking(true);
                        await speak(phrase.english, enVoice.lang, 1.0, enVoice.voiceURI, null);
                        await new Promise(res => setTimeout(res, 400));
                    } catch (err) {
                        /* English playback is optional — ignore failures, but track them */
                        reportError('tts_english_failed', {
                            error_type: 'english_tts_failed',
                            mode: 'automatic',
                            tts_error_code: err?.error || err?.message || 'unknown',
                        });
                    }
                    finally { setEnglishSpeaking(false); }
                } else if (allVoices.length === 0) {
                    // Voice list unavailable even after loadVoices() (some engines
                    // never report). Attempt the speech by lang anyway — the engine
                    // usually still picks an English voice from utterance.lang.
                    try {
                        if (i === startIdx) await new Promise(res => setTimeout(res, 1200));
                        if (isStopped()) break;
                        setEnglishSpeaking(true);
                        await speak(phrase.english, 'en-GB', 1.0, null, null);
                        await new Promise(res => setTimeout(res, 400));
                    } catch (err) {
                        reportError('tts_english_failed', {
                            error_type: 'english_tts_failed',
                            mode: 'automatic',
                            tts_error_code: err?.error || err?.message || 'unknown',
                        });
                    }
                    finally { setEnglishSpeaking(false); }
                } else if (!englishWarnedRef.current) {
                    englishWarnedRef.current = true;
                    const langs = [...new Set(allVoices.map(v => v.lang))].join(', ') || 'none';
                    reportError('tts_no_english',{ error_type: 'no_english_voice', mode: 'automatic', installed_voices: langs });
                    toast.error(`No English voice installed on this device, so the translation can't be spoken in English. Installed voices: ${langs}. Add an English voice in your device's text-to-speech settings.`);
                }
                if (isStopped()) break;
                phraseProgressRef.current.englishDone = true;
            }

            let duration = 1500;
            const repeats = getSavedPlaybacks();
            // Skip the whole French-listen block when a resumed phrase already
            // completed it — otherwise pause after recording would replay every
            // TTS repeat before letting the learner speak again.
            if (!phraseProgressRef.current.listenDone) {
                for (let r = 0; r < repeats; r++) {
                    if (isStopped()) break;
                    try {
                        wordBoundariesRef.current = [];
                        // Capture the exact live-highlight timings so the recording/playback
                        // replay reproduces what was shown during TTS — identical method to
                        // Practice mode, so highlighting is consistent across every mode.
                        const ttsStart = Date.now();
                        const onBoundary = (charIndex) => {
                            if (charIndex !== -1) wordBoundariesRef.current.push({ charIndex, elapsed: Date.now() - ttsStart });
                            setActiveCharIndex(charIndex);
                        };
                        duration = await speakFrench(phrase.french, speed, voice || null, onBoundary) || 1500;
                        logEvent('TTS end', `duration=${duration}ms, speed=${speed}, repeat ${r+1}/${repeats}`);
                        practiceApi.recordListen(phrase.id, mode).catch(() => {});
                        posthog.capture('phrase_listened', { phrase_id: phrase.id, source: 'listen_mode', mode });
                    } catch (err) {
                        // Surface the real cause once so it's diagnosable instead of
                        // silently skipping straight to recording.
                        if (r === 0) {
                            reportError('tts_failed', {
                                error_type: 'tts_playback_failed',
                                mode: 'automatic',
                                tts_error_code: err?.error || err?.message || 'unknown',
                                voice_uri: voice || '(default)',
                            });
                            toast.error(`Voice playback unavailable (${err?.error || err?.message || 'unknown'}). Try again, or install a French voice in your device settings.`);
                        }
                    }
                    // Brief gap between repeats (except after the last one)
                    if (r < repeats - 1 && !isStopped()) {
                        setActiveCharIndex(-1);
                        await new Promise(res => setTimeout(res, 600));
                    }
                }
                if (isStopped()) break;
                setActiveCharIndex(-1);
                phraseProgressRef.current.listenDone = true;
            }

            // Reusable pre-warm helper — MIC IS DELIBERATELY OPENED AFTER THE
            // BEEP now. Opening it earlier put Chrome/Android into a "voice
            // communications" audio session that muted the WebAudio beep
            // (worked in listen-only where no mic ever opens). Beep first,
            // then open the mic and start recording right after.
            let preWarmed = false;
            const doPreWarm = async (constraints) => {
                audioRecorderRef.current = new AudioRecorder();
                audioRecorderRef.current.onAutoStopped = () => toast.error(RECORDING_CAPPED_MESSAGE);
                const lent = sessionMicRef.current;
                if (lent && lent.getAudioTracks().some(t => t.readyState === 'live')) {
                    preWarmed = audioRecorderRef.current.useStream(lent);
                    return;
                }
                const preWarmResult = await audioRecorderRef.current.preWarm(constraints);
                preWarmed = preWarmResult === true;
                if (!preWarmed) {
                    const name = typeof preWarmResult === 'string' ? preWarmResult : '';
                    const denied = name === 'NotAllowedError' || name === 'PermissionDeniedError';
                    const missing = name === 'NotFoundError' || name === 'DevicesNotFoundError';
                    reportError(denied ? 'mic_denied' : missing ? 'mic_missing' : 'mic_failed', {
                        error_type: denied ? 'permission_denied' : missing ? 'no_microphone_found' : 'mic_access_failed',
                        mode: 'automatic',
                        error_name: name,
                        error_message: audioRecorderRef.current?.lastErrorMessage || '',
                        secure_context: window.isSecureContext,
                    });
                }
                logEvent('Pre-warm complete', `preWarmed=${preWarmed}`);
            };

            // 1.4× the phrase's TTS length gives the learner a bit of breathing
            // room. Hard-capped at the recorder's 29s limit so we never ask them
            // to speak longer than Speechace can score in a single call — that
            // would silently downgrade the clip to Gemini and make scoring
            // inconsistent across phrase lengths.
            const wantedMs = Math.max(duration * 1.4, 2000);
            const gapMs = Math.min(wantedMs, MAX_RECORDING_MS);
            // The phrase needs longer than we allow, so they are going to be cut
            // off mid-sentence. Manual mode has always said so; hands-free just
            // stopped, which reads as the app breaking rather than a phrase that
            // is too long. Saying it here is the nudge to shorten the phrase.
            if (wantedMs > MAX_RECORDING_MS && (mode === 'listen-record' || mode === 'listen-repeat')) {
                toast.error(RECORDING_CAPPED_MESSAGE);
            }

            if ((mode === 'listen-record' || mode === 'listen-repeat') && countdownEnabledRef.current && !phraseProgressRef.current.countdownDone) {
                logEvent('Countdown start');
                setPhase('countdown');
                for (let count = 3; count >= 1; count--) {
                    if (isStopped()) break;
                    setCountdownValue(count);
                    await wait(1000);
                }
                if (isStopped()) break;
                setCountdownValue('go');
                logEvent('Countdown end');
                phraseProgressRef.current.countdownDone = true;
            }

            if (mode === 'listen-record') {
                // Phase 2: Beep BEFORE opening the mic. Chrome/Android put an
                // open mic into a voice-communications audio session that
                // silences the WebAudio beep — worked around by playing the
                // beep first, then opening the mic and starting recording.
                // Test accounts: the mic opens BEFORE the beep, without echo
                // cancellation (see MIC_CONSTRAINTS_EARLY), so recording starts
                // the moment the beep ends and the first word is not lost.
                // Everyone else keeps the old order until it is confirmed on
                // Android that the beep is still heard.
                // Android only for now: on desktop Chrome, Automatic recordings had
                // ~0.7 s of silence about 1 s in, and this tests whether the early
                // mic is the cause (19 Oct 2026). Manual never uses it.
                const earlyMic = !!user?.early_mic && /Android/i.test(navigator.userAgent || '');
                // Test accounts: a timeline of this recording (see newRecDiag).
                const recDiag = newRecDiag('automatic');
                recDiag?.mark('record_phase', { early_mic: earlyMic, gap_ms: gapMs, speed: getSavedSpeed() });
                if (earlyMic) { recDiag?.mark('mic_open_begin'); await doPreWarm(MIC_CONSTRAINTS_EARLY); recDiag?.mark('mic_open_done', { ok: preWarmed }); }
                unlockAudioContext();
                recDiag?.mark('beep_begin');
                await playBeep();
                recDiag?.mark('beep_done');
                await wait(80); // let the beep tail finish before recording
                if (!earlyMic) { recDiag?.mark('mic_open_begin'); await doPreWarm(); recDiag?.mark('mic_open_done', { ok: preWarmed }); }
                if (isStopped()) break;
                if (audioRecorderRef.current) audioRecorderRef.current.diag = recDiag;
                const recStarted = preWarmed && audioRecorderRef.current
                    ? audioRecorderRef.current.startFromStream()
                    : false;
                logEvent('Beep + recording start', `gapMs=${gapMs}, recStarted=${recStarted}`);
                recordingDurationRef.current = gapMs;
                recStartRef.current = Date.now();
                setPhase('recording');
                replayHighlighting(wordBoundariesRef.current);
                const stream = audioRecorderRef.current?.stream;
                const endedBy = await Promise.race([
                    wait(gapMs).then(() => 'timer'),
                    stream ? waitForSilence(stream, { minSpeechMs: 500, silenceMs: 1200, threshold: 0.01, diag: recDiag, adaptive: true })
                        .then(r => `silence_check:${r}`) : wait(gapMs).then(() => 'timer'),
                ]);
                recDiag?.mark('record_end', { by: endedBy });
                clearHighlightTimers();
                setActiveCharIndex(-1);
                // Brief tail so an auto-stop (silence detected) doesn't feel abrupt
                // and the recorder captures the final syllable.
                await wait(350);
                if (isStopped()) break;

                let audioUrl = null, audioBlob = null, mimeType = null;
                if (recStarted && audioRecorderRef.current) {
                    try {
                        const stopped = await audioRecorderRef.current.stop();
                        audioUrl = stopped?.url || null;
                        audioBlob = stopped?.blob || null;
                        mimeType = stopped?.mimeType || null;
                    } catch { /* silent */ }
                }
                recDiag?.mark('stopped', { bytes: audioBlob?.size || 0 });
                logEvent('Recording stop');

                if (audioBlob) posthog.capture('phrase_recorded', { phrase_id: phrase.id, source: 'listen_mode', mode });

                // Fire analysis immediately so it runs concurrently with playback
                const analysisPromise = audioBlob
                    ? analyzeAudioApi.analyzeAudio(phrase.french, audioBlob, mimeType, recDiag)
                    : null;

                // Phase 3: Play back recorded audio — replay timing so user can see if they kept up
                if (audioUrl) {
                    logEvent('Playback start');
                    // Use the real recorded length (capped to gapMs) so the progress
                    // line matches the clip rather than a fixed estimate.
                    playbackDurationRef.current = Math.min(Date.now() - recStartRef.current, gapMs) || gapMs;
                    setPhase('playback');
                    await playbackRecording(audioUrl, () => {
                        // Both anchored to onplaying so the line and the highlight
                        // start exactly when the voice does.
                        startPlaybackProgress(playbackDurationRef.current);
                        replayHighlighting(wordBoundariesRef.current);
                    });
                    clearHighlightTimers();
                    setActiveCharIndex(-1);
                    if (isStopped()) break;
                }

                // Phase 4: Await analysis (already running since recording stopped)
                logEvent('Analysis start');
                setPhase('analyzing');
                let analyzeOk = false;
                let score = 0;
                let feedback = '';
                let transcriptHeard = '';
                let analysisData = null;
                if (!audioBlob) {
                    console.error('[Mimique] Analysis skipped — audioBlob is null (recording may have failed)');
                    reportError('recording_empty',{ error_type: 'recording_failed', mode: 'automatic' });
                } else {
                    try {
                        const res = await Promise.race([
                            analysisPromise,
                            new Promise((_, reject) =>
                                setTimeout(() => reject(new Error('Analysis timed out')), 15000)
                            ),
                        ]);
                        analysisData = res.data;
                        // "No speech detected" is a mic/recording problem, not
                        // pronunciation — force the score to 0 (shown + saved),
                        // matching Manual.
                        if (analysisData.audio_issue && /no speech/i.test(analysisData.audio_issue)) {
                            analysisData.overall_score = 0;
                        }
                        score = analysisData.overall_score ?? null;  // null = too quiet to score
                        feedback = analysisData.feedback || '';
                        transcriptHeard = analysisData.transcript_heard || '';
                        analyzeOk = true;
                        posthog.capture('feedback_received', { phrase_id: phrase.id, score, match_quality: res.data.match_quality, source: 'listen_mode', mode, ...feedbackShownProps(phrase, res.data) });
                    } catch (err) {
                        console.error('[Mimique] Analysis API call failed:', err?.response?.status, err?.response?.data || err?.message || err);
                        reportError('analysis_failed',{ error_type: 'analysis_failed', mode: 'automatic', clip_mime: mimeType, clip_bytes: audioBlob?.size, http_status: err?.response?.status, error: requestFailureReason(err) });
                    }
                }
                logEvent('Analysis end', `analyzeOk=${analyzeOk}, score=${score}`);
                if (isStopped()) break;

                if (!analyzeOk) {
                    setPhase('analysis-error');
                    phraseCompletedRef.current = true;
                    await wait(6000);
                    if (isStopped()) break;
                    continue;
                }

                if (analysisData?.audio_issue) {
                    const loud = await measureLoudness(audioBlob);
                    const silent = /no speech/i.test(analysisData.audio_issue);
                    const props = {
                        error_type: silent ? 'no_speech_detected' : 'recording_issue',
                        mode: 'automatic',
                        audio_issue: analysisData.audio_issue,
                        loudness_rms: loud.rms,
                        loudness_peak: loud.peak,
                    };
                    // Quiet recordings are normal (iOS records ~50x lower), so they're a
                    // plain analytics event, not an Error Tracking issue.
                    if (silent) reportError('recording_silent', props);
                    else posthog.capture('recording_quiet', props);
                }

                // Save practice record & session stats — but skip recordings that
                // were too quiet to score (score === null): they aren't real attempts
                // and would drag the session average down / show up as "needs work".
                if (score !== null) {
                    try {
                        await practiceApi.save({
                            phrase_id: phrase.id,
                            score,
                            feedback,
                            spoken_text: transcriptHeard,
                            // Returned with the record so a sound can be
                            // tracked across the month. Speechace measures every
                            // phoneme on every recording; until now the app read
                            // it out once and binned it.
                            sounds: analysisData.sounds || null,
                            score_source: analysisData.score_source || null,
                            word_scores: analysisData.word_scores || null,
                            // No duration: practice minutes are timed while
                            // listening and speaking only (lib/practiceTime.js).
                        });
                    } catch { /* silent */ }

                    setSessionStats(prev => ({
                        practiced: prev.practiced + 1,
                        totalScore: prev.totalScore + score,
                    }));
                    keepResult({
                        phrase,
                        score,
                        feedback,
                        issues: Array.isArray(analysisData.issues) ? analysisData.issues : [],
                        phone_tips: Array.isArray(analysisData.phone_tips) ? analysisData.phone_tips : [],
                        stress_tips: Array.isArray(analysisData.stress_tips) ? analysisData.stress_tips : [],
                        word_scores: Array.isArray(analysisData.word_scores) ? analysisData.word_scores : [],
                    });
                } else if (analysisData?.score_locked) {
                    // A real practice with no score because the free allowance is
                    // used up. Kept, so the session ends on its summary rather than
                    // the home screen, and never saved as a record: there is no
                    // score to save.
                    keepResult({
                        phrase,
                        score: null,
                        locked: true,
                        lockedMessage: analysisData.locked_message || '',
                        issues: [], phone_tips: [], stress_tips: [],
                    });
                } else if (analysisData) {
                    // Practised but not scored (cut off, too quiet): listed on the
                    // summary with no score and kept out of the average, the same
                    // as Manual. It used to be dropped, so 4 phrases read as 2.
                    keepResult({
                        phrase,
                        score: null,
                        issues: [], phone_tips: [], stress_tips: [],
                    });
                }

                // Quick Practice: stop here and show the full results card (score, Retry,
                // Back to Phrases) instead of the transient score flash + idle screen.
                if (singlePhraseId) {
                    setSingleResult(analysisData);
                    setSingleRecordingUrl(audioUrl);
                    setSingleRecordingBlob(audioBlob || null);
                    phraseCompletedRef.current = true;
                    break;
                }

                // Phase 5: Show score — duration scales with feedback word count so
                // there's time to actually read the pronunciation feedback.
                setLastScore({
                    score,
                    feedback,
                    audioIssue: analysisData.audio_issue || '',
                    issues: Array.isArray(analysisData.issues) ? analysisData.issues : [],
                    phoneTips: Array.isArray(analysisData.phone_tips) ? analysisData.phone_tips : [],
                    stressTips: Array.isArray(analysisData.stress_tips) ? analysisData.stress_tips : [],
                    wordScores: Array.isArray(analysisData.word_scores) ? analysisData.word_scores : [],
                    missedWords: Array.isArray(analysisData.missed_words) ? analysisData.missed_words : [],
                    missedAtEnd: !!analysisData.missed_at_end,
                    missedAtStart: !!analysisData.missed_at_start,
                    missedDetail: Array.isArray(analysisData.missed_detail) ? analysisData.missed_detail : [],
                    phrase: phrase.french,
                    // The recording itself, so a flagged word can be played back
                    // in their own voice. The blob rather than a url for it: a url
                    // can be revoked while the score is still on screen.
                    recording: audioBlob || null,
                    scoreLocked: !!analysisData.score_locked,
                    lockedMessage: analysisData.locked_message || '',
                });
                const displayedText = analysisData.audio_issue || feedback;
                const wordCount = displayedText.trim().split(/\s+/).filter(Boolean).length;
                // Scales with how much there actually is to read — a "Perfect!"
                // flash with no tips lingers ~2s; a longer feedback with several
                // phoneme tips gets closer to 10s. The Next → button lets any
                // reader skip forward the moment they're done.
                const tipsCount = (
                    (Array.isArray(analysisData.issues) ? analysisData.issues.length : 0)
                    + (Array.isArray(analysisData.phone_tips) ? analysisData.phone_tips.length : 0)
                    + (Array.isArray(analysisData.stress_tips) ? analysisData.stress_tips.length : 0)
                );
                const scoreDisplayMs = Math.max(2000, Math.min(10000, wordCount * 300 + tipsCount * 900));
                scoreDurationRef.current = scoreDisplayMs;
                setPhase('score');
                phraseCompletedRef.current = true;
                // Auto-timer, OR early exit if the learner taps Next → because
                // they've already finished reading. Whichever fires first wins.
                await Promise.race([
                    wait(scoreDisplayMs),
                    new Promise(resolve => { scoreSkipRef.current = resolve; }),
                ]);
                scoreSkipRef.current = null;
                if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
                if (isStopped()) break;
                setLastScore(null);

            } else if (mode === 'listen-repeat') {
                // Same beep-before-mic-open pattern as listen-record.
                unlockAudioContext();
                await playBeep();
                await wait(80);
                await doPreWarm();
                if (isStopped()) break;
                const recStarted = preWarmed && audioRecorderRef.current
                    ? audioRecorderRef.current.startFromStream()
                    : false;
                logEvent('Beep + recording start', `gapMs=${gapMs}, recStarted=${recStarted}`);
                recordingDurationRef.current = gapMs;
                recStartRef.current = Date.now();
                setPhase('recording');
                replayHighlighting(wordBoundariesRef.current);
                const repeatStream = audioRecorderRef.current?.stream;
                await Promise.race([
                    wait(gapMs),
                    repeatStream ? waitForSilence(repeatStream, { minSpeechMs: 500, silenceMs: 1200, threshold: 0.01, adaptive: true }) : wait(gapMs),
                ]);
                clearHighlightTimers();
                setActiveCharIndex(-1);
                // Brief tail so an auto-stop (silence detected) doesn't feel abrupt.
                await wait(350);
                if (isStopped()) break;

                let audioUrl = null, repeatBlob = null;
                if (recStarted && audioRecorderRef.current) {
                    try {
                        const stopped = await audioRecorderRef.current.stop();
                        audioUrl = stopped?.url || null;
                        repeatBlob = stopped?.blob || null;
                    } catch { /* silent */ }
                }
                logEvent('Recording stop');
                if (repeatBlob) posthog.capture('phrase_recorded', { phrase_id: phrase.id, source: 'listen_mode', mode });
                else reportError('recording_empty',{ error_type: 'recording_failed', mode: 'automatic' });

                // Phase 3: Play back recorded audio
                if (audioUrl) {
                    logEvent('Playback start');
                    // Use the real recorded length (capped to gapMs) so the progress
                    // line matches the clip rather than a fixed estimate.
                    playbackDurationRef.current = Math.min(Date.now() - recStartRef.current, gapMs) || gapMs;
                    setPhase('playback');
                    await playbackRecording(audioUrl, () => {
                        // Both anchored to onplaying so the line and the highlight
                        // start exactly when the voice does.
                        startPlaybackProgress(playbackDurationRef.current);
                        replayHighlighting(wordBoundariesRef.current);
                    });
                    clearHighlightTimers();
                    setActiveCharIndex(-1);
                    if (isStopped()) break;
                }

                phraseCompletedRef.current = true;

            } else {
                // listen-only: pause → beep → small gap before next phrase
                phraseCompletedRef.current = true;
                await wait(1000);
                if (isStopped()) break;
                // Re-arm the AudioContext right before each beep. iOS quietly
                // suspends contexts between phrases (especially when TTS just
                // finished), and playBeep()'s internal resume() can miss —
                // an explicit unlock keeps the beep audible session-long.
                unlockAudioContext();
                await playBeep();
                await wait(600);
                if (isStopped()) break;
            }
        }

        // The session's mic goes off when the run ends, finished or stopped.
        // Not when a newer run replaced this one: that mic is the new run's.
        if (runIdRef.current !== runId) return;
        closeSessionMic();

        if (!isStopped()) {
            if (segmentStartRef.current) {
                sessionDurationRef.current += Date.now() - segmentStartRef.current;
                segmentStartRef.current = null;
            }
            posthog.capture('session_finished', {
                session_mode: mode,
                phrases_total: phrasesRef.current.length,
                duration_seconds: Math.round(sessionDurationRef.current / 1000),
            });
            // Unlocks the add-to-home-screen prompt: they've now felt what the
            // app does, so it's worth an icon to them.
            markPractised();
            setCurrentIndex(phrasesRef.current.length);
            setPhase('idle');
            setIsActive(false);
            // Scored session: show a pronunciation summary instead of the generic text.
            if (!singlePhraseId && mode === 'listen-record' && sessionResultsRef.current.length > 0) {
                setSessionSummary(sessionResultsRef.current.slice());
            } else if (!singlePhraseId) {
                // Non-scoring modes: show a stats summary (phrases + time) instead.
                setStatsSummary({
                    count: phrasesRef.current.length,
                    durationSecs: Math.round(sessionDurationRef.current / 1000),
                    mode,
                });
            }
        }
    };

    // Start (or restart) a run over the given phrase list. Restart uses the full set;
    // "redo low scores" passes just the low-scoring phrases.
    const startRun = (list) => {
        if (!list || list.length === 0) return;
        phrasesRef.current = list;
        setPhrases(list);
        setCurrentIndex(0);
        setSessionSummary(null);
        setStatsSummary(null);
        sessionResultsRef.current = [];
        setSessionStats({ practiced: 0, totalScore: 0 });
        sessionDurationRef.current = 0;
        segmentStartRef.current = Date.now();
        posthog.capture('session_started_hands_free', { session_mode: mode, phrases_total: list.length });
        runSession(0, playbackSpeed, getSavedTTSVoice());
    };

    const handleStart = () => { unlockAudioPlayback(); unlockAudioContext(); warmUpSpeech(); startRun(allPhrasesRef.current); };

    const handleRedoLowScores = () => {
        const low = (sessionSummary || []).filter(r => r.score != null && r.score < REDO_BELOW).map(r => r.phrase);
        const unique = [...new Map(low.map(p => [p.id, p])).values()];
        startRun(unique);
    };

    // Order a phrase list the same way fetchPhrases does (smart / random / added).
    const orderPhrases = (list) => {
        const unique = [...new Map(list.map(p => [p.id, p])).values()];
        if (order === 'random') {
            for (let i = unique.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [unique[i], unique[j]] = [unique[j], unique[i]];
            }
        } else if (order === 'added') {
            unique.sort((a, b) => (a.created_at || '') < (b.created_at || '') ? -1 : (a.created_at || '') > (b.created_at || '') ? 1 : 0);
        } else {
            unique.sort((a, b) => {
                const aNever = (a.practice_count || 0) === 0;
                const bNever = (b.practice_count || 0) === 0;
                if (aNever !== bNever) return aNever ? -1 : 1;
                const scoreDiff = (a.average_score || 0) - (b.average_score || 0);
                if (scoreDiff !== 0) return scoreDiff;
                const aLast = a.last_practiced || '';
                const bLast = b.last_practiced || '';
                return aLast < bLast ? -1 : aLast > bLast ? 1 : 0;
            });
        }
        return unique;
    };

    // Back button: confirm before leaving if a session is in progress.
    const handleBackClick = () => {
        if (isActive || isPaused) {
            setShowLeaveConfirm(true);
        } else {
            handleStop();
            navigate(returnTo);
        }
    };

    const confirmLeave = () => {
        setShowLeaveConfirm(false);
        handleStop();
        navigate(returnTo);
    };

    // Pick a different collection from the end-of-session screen and start a fresh run.
    const startCollectionSession = async (collectionId) => {
        try {
            const res = collectionId === '__all__'
                ? await phrasesApi.getAll()
                : await phrasesApi.getAll(collectionId);
            const ordered = orderPhrases(res.data);
            if (ordered.length === 0) {
                toast.error('No phrases in that collection.');
                return;
            }
            setCollectionLabel(collectionId === '__all__'
                ? 'All Phrases'
                : (collections.find(c => c.id === collectionId)?.name || ''));
            allPhrasesRef.current = ordered;
            startRun(ordered);
        } catch {
            toast.error('Failed to load that collection.');
        }
    };

    const handlePause = () => {
        if (segmentStartRef.current) {
            sessionDurationRef.current += Date.now() - segmentStartRef.current;
            segmentStartRef.current = null;
        }
        stoppedRef.current = true;
        cleanup();
        setIsPaused(true);
        setIsActive(false);
        // If phrase was fully completed, resume from next one AS A FRESH
        // phrase — its English/French/countdown sub-phases must all replay,
        // so mark isResumingRef false. Otherwise we're mid-phrase and want
        // to skip the sub-phases already completed on resume.
        if (phraseCompletedRef.current) {
            resumeIndexRef.current = Math.min(resumeIndexRef.current + 1, phrases.length);
            pausedMidPhraseRef.current = false;
        } else {
            pausedMidPhraseRef.current = true;
        }
    };

    const handleResume = async () => {
        unlockAudioPlayback();  // re-unlock TTS + recorded playback in this gesture
        unlockAudioContext(); // re-arm the beep context within this Resume gesture
        segmentStartRef.current = Date.now();
        // Brief hold so the paused score view lingers a beat before the next
        // phrase kicks in — removes the abrupt "jump" from score-paused straight
        // into new-phrase audio. isPaused stays true throughout so the paused
        // view keeps rendering; runSession flips it back below.
        await new Promise(r => setTimeout(r, 500));
        setLastScore(null);
        // Only preserve sub-phase progress when we're resuming the SAME phrase
        // that was mid-flow. If pause advanced us to the next phrase (the
        // previous one had completed), that new phrase must play its full
        // intro from scratch — English, French listen, countdown, all of it.
        isResumingRef.current = pausedMidPhraseRef.current;
        pausedMidPhraseRef.current = false;
        runSession(resumeIndexRef.current, playbackSpeed, getSavedTTSVoice());
    };

    // Back, forward and Try Again show only on the feedback screen and while
    // paused (Leanne, 4 Nov 2026): never while the phrase plays, she records,
    // or her own recording plays back, so nothing cuts a take short.
    const navShown = !singlePhraseId && ((phase === 'score' && !!lastScore && !isPaused) || isPaused);
    const canGoBack = navShown && currentIndex > 0;
    // On the feedback screen forward also finishes the session from the last
    // phrase, as it always has; from pause it only moves to a phrase that exists.
    const canGoForward = navShown && (!isPaused || currentIndex + 1 < phrases.length);

    // Stop what is happening and start phrase `target` from its beginning.
    // From pause it plays straight away: the tap is the go-ahead, like Resume.
    const jumpToPhrase = (target) => {
        if (target < 0 || target >= phrasesRef.current.length) return;
        unlockAudioPlayback();  // in this gesture, as Resume does, for iOS
        stoppedRef.current = true;
        cleanup();
        unlockAudioContext();   // after cleanup, which closes the beep context
        playbackSkipRef.current = null;
        scoreSkipRef.current = null;
        isResumingRef.current = false;
        pausedMidPhraseRef.current = false;
        if (!segmentStartRef.current) segmentStartRef.current = Date.now();
        setLastScore(null);
        runSession(target, playbackSpeed, getSavedTTSVoice());
    };
    const handlePrevPhrase = () => jumpToPhrase(currentIndex - 1);
    const handleTryAgain = () => jumpToPhrase(currentIndex);
    // On the feedback screen, end the score linger and carry on as the timer
    // would. From pause, start the next phrase.
    const handleNextArrow = () => {
        if (!isPaused && scoreSkipRef.current) scoreSkipRef.current();
        else jumpToPhrase(currentIndex + 1);
    };

    const handleStop = () => {
        if (isActive || isPaused) {
            const durationMs = sessionDurationRef.current + (segmentStartRef.current ? Date.now() - segmentStartRef.current : 0);
            const completedCount = resumeIndexRef.current + (phraseCompletedRef.current ? 1 : 0);
            posthog.capture('session_quit', {
                session_mode: mode,
                phrases_total: phrases.length,
                phrases_completed: completedCount,
                duration_seconds: Math.round(durationMs / 1000),
            });
        }
        segmentStartRef.current = null;
        stoppedRef.current = true;
        cleanup();
        setIsActive(false);
        setIsPaused(false);
        setPhase('idle');
        setLastScore(null);
        setEnglishSpeaking(false);
    };

    const handleSpeedChange = (speed) => {
        setPlaybackSpeed(speed);
        // deliberately not persisting to localStorage — speed change is session-only
    };


    const currentPhrase = phrases[currentIndex];
    const currentSinglePhrase = phrases[0] || null;
    const progress = phrases.length > 0 ? ((currentIndex + (isActive ? 1 : 0)) / phrases.length) * 100 : 0;
    const isRecordMode = mode === 'listen-record';
    const isRepeatMode = mode === 'listen-repeat';
    const isListenOnly = mode === 'listen-only';

    // Quick Practice (single phrase) is always the record+analyse flow launched from
    // a phrase, so just label it "Automatic" rather than the full mode name.
    const modeLabel = singlePhraseId ? 'Automatic' : isRecordMode ? 'Listen, Repeat & Analysis' : isRepeatMode ? 'Listen & Repeat' : 'Listen Only';
    const modeColor = isRecordMode ? 'rose' : isRepeatMode ? 'purple' : 'teal';
    const ModeIcon = isRecordMode ? Repeat : isRepeatMode ? Mic : Headphones;
    const orderLabel = order === 'random' ? 'Random' : order === 'added' ? 'Order added' : 'Smart';

    // Quick Practice results card actions
    const handleSingleRetry = () => {
        if (singlePlayerRef.current) { singlePlayerRef.current.pause(); singlePlayerRef.current = null; }
        setIsPlayingSingle(false);
        setSingleRecordingUrl(null);
        setSingleRecordingBlob(null);
        setSingleResult(null);
        setCurrentIndex(0);
        handleStart();
    };

    const handlePlaySingleRecording = () => {
        if (!singleRecordingUrl) return;
        if (isPlayingSingle && singlePlayerRef.current) {
            singlePlayerRef.current.pause();
            singlePlayerRef.current.currentTime = 0;
            setIsPlayingSingle(false);
            return;
        }
        const audio = new Audio(singleRecordingUrl);
        singlePlayerRef.current = audio;
        audio.onended = () => { setIsPlayingSingle(false); singlePlayerRef.current = null; };
        audio.onerror = () => { setIsPlayingSingle(false); singlePlayerRef.current = null; };
        setIsPlayingSingle(true);
        playRecording(audio, () => {
            setIsPlayingSingle(false);
            singlePlayerRef.current = null;
            toast.error(PLAYBACK_FAILED_MESSAGE);
        });
    };

    // Play the native TTS of the target phrase so the learner can compare it to
    // their own recording. Triggered by a tap, so speechSynthesis works on iOS.
    const handlePlayTarget = async () => {
        if (isPlayingTarget) {
            window.speechSynthesis.cancel();
            cancelFrench();
            setIsPlayingTarget(false);
            return;
        }
        if (!currentSinglePhrase) return;
        // Unlock inside the tap: the TTS fetch below outlives the gesture, so
        // without this iOS refuses the natural voice and drops to the device one.
        unlockAudioPlayback();
        setIsPlayingTarget(true);
        try {
            await speakFrench(currentSinglePhrase.french, playbackSpeed, getSavedTTSVoice(), null);
        } catch { /* ignore playback errors */ }
        finally { setIsPlayingTarget(false); }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-foreground">
                <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // Pause / Resume. Under the phrase while it runs; on the score it moves up
    // under the score and its message, with the timer, because the word card
    // and feedback pushed it off a phone screen (Leanne, 27 Oct 2026).
    const pauseControl = isPaused ? (
        <button
            type="button"
            data-testid="session-resume-btn"
            onClick={handleResume}
            aria-label="Resume session"
            title="Resume"
            className="inline-flex items-center gap-1.5 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground px-4 py-1.5 text-xs font-semibold shadow-sm"
        >
            <Play className="h-3.5 w-3.5" />
            Resume
        </button>
    ) : (
        <button
            type="button"
            data-testid="session-pause-btn"
            onClick={handlePause}
            aria-label="Pause session"
            title="Pause"
            className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 px-4 py-1.5 text-xs font-semibold shadow-sm"
        >
            <Pause className="h-3.5 w-3.5" />
            Pause
        </button>
    );
    // Pause or Resume with the back and forward arrows either side of it, in
    // one row (Leanne, 4 Nov 2026), and Try Again underneath. The arrows and
    // Try Again show on the feedback screen and while paused only (navShown).
    // A missing arrow keeps its space so Pause/Resume stays centred.
    // The one timeline, for whichever phase is timed: recording, playback of
    // their own voice, or the feedback linger. Each used to draw its own bar
    // in a different spot (Leanne, 4 Nov 2026). It sits under Pause/Resume in
    // every phase, and keeps its space when nothing is timed so the card does
    // not jump as phases change.
    const timeline = isPaused ? null
        : phase === 'recording' ? { pct: recordingProgress, color: recordingProgress > 80 ? 'bg-amber-500' : 'bg-rose-400' }
        : phase === 'playback' ? { pct: playbackProgress, color: 'bg-primary' }
        : (phase === 'score' && lastScore) ? { pct: scoreProgress, color: 'bg-primary' }
        : null;
    const sessionControls = (
        <div className="flex flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-3">
                {navShown && (canGoBack
                    ? <NextPhraseButton inline back testId="session-prev-btn" onClick={handlePrevPhrase} />
                    : <span className="w-10" aria-hidden="true" />)}
                {pauseControl}
                {navShown && (canGoForward
                    ? <NextPhraseButton inline testId="score-next-btn" onClick={handleNextArrow} />
                    : <span className="w-10" aria-hidden="true" />)}
            </div>
            {navShown && <TryAgainButton small testId="session-try-again-btn" onClick={handleTryAgain} />}
            {!isPaused && (
                <div className={`w-full max-w-[220px] rounded-full h-1.5 overflow-hidden mt-1 ${timeline ? 'bg-stone-200' : ''}`}
                     data-testid="session-timeline">
                    {timeline && <div className={`h-full rounded-full transition-none ${timeline.color}`}
                                      style={{ width: `${timeline.pct}%` }} />}
                </div>
            )}
        </div>
    );
    const scoreShowing = (phase === 'score' || isPaused) && !!lastScore;
    // The word tips, on the score and while paused, straight under the phrase
    // (PhraseDisplay's belowPhrase), above Show phonetics, Pause and the Paused
    // label (Leanne, 4 Nov 2026). Speechace's phoneme and stress data, with
    // Gemini's guessed issues as the fallback when Speechace did not run, and
    // shown on a cut-off too: partial audio still gives useful tips. Beta
    // accounts tap a word for its card (tapping pauses the run).
    const scoreTips = scoreShowing && (
        <div className="w-full max-w-sm mx-auto mt-3" data-testid="phase-score-tips">
            {(pronunciationBeta() && !lastScore.scoreLocked)
                ? <PickedWordTips phoneTips={lastScore.phoneTips || []}
                                  stressTips={lastScore.stressTips || []}
                                  issues={lastScore.issues || []}
                                  picked={pickedWord} dense
                                  missed={lastScore.missedDetail || []}
                                  missedAtEnd={!!lastScore.missedAtEnd}
                                  missedAtStart={!!lastScore.missedAtStart}
                                  phrase={currentPhrase?.french || ''}
                                  wordScores={lastScore.wordScores || []} />
                : <PronunciationTips
                    phoneTips={lastScore.phoneTips || []}
                    stressTips={lastScore.stressTips || []}
                    issues={lastScore.issues || []}
                    title=""
                    dense
                    scoreLocked={!!lastScore.scoreLocked}
                    phrase={currentPhrase?.french || ''}
                    wordScores={lastScore.wordScores || []}
                    recordedAudio={lastScore.recording || null}
                />}
        </div>
    );

    return (
        <div className="min-h-screen bg-fixed bg-gradient-to-b from-foreground to-primary text-primary-foreground" data-testid="session-page">
            <SessionHeader title={singlePhraseId ? 'Quick Practice' : (collectionLabel || 'All Phrases')}>
                <Badge variant="outline" className="border-accent text-accent bg-transparent" data-testid="session-mode-badge">
                    <ModeIcon className="h-3 w-3 mr-1" /> {modeLabel}
                </Badge>
                {!singlePhraseId && (
                    <Badge variant="outline" className="border-primary-foreground/40 text-primary-foreground bg-transparent" data-testid="session-order-badge">
                        {order === 'random' ? <Sparkles className="h-3 w-3 mr-1" /> : <Target className="h-3 w-3 mr-1" />}
                        {orderLabel}
                    </Badge>
                )}
            </SessionHeader>

            <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
                {singleResult ? (
                    /* Quick Practice results — mirrors the Manual results card */
                    <>
                    <Card className="bg-white border-stone-100 shadow-lg animate-fade-in" data-testid="single-result-card">
                        <CardContent className="p-6 md:p-8">
                            <ScoreHeader score={singleResult.overall_score} locked={!!singleResult.score_locked}
                                         onUpgrade={() => setUpgradePrompt(singleResult.locked_message)} />
                            {/* A recording problem, or the feedback, straight under the score and
                                above the phrase, the same order as Manual (Leanne, 27 Oct 2026). */}
                            {singleResult.audio_issue ? (
                                <RecordingNotice audioIssue={singleResult.audio_issue} score={singleResult.overall_score} mode="automatic" />
                            ) : singleResult.score_locked ? null : (
                            /* No Feedback box without a score: nothing was
                               graded, and the fallback sentence for an empty
                               result read "Significant differences from the
                               target phrase", telling a free user they had
                               got it wrong. */
                            <FeedbackBox className="mb-3">{singleResult.feedback?.trim() || {
                                    exact: 'Spot on! Your pronunciation closely matches the target.',
                                    close: 'Very close, just a few minor adjustments needed.',
                                    partial: 'Good attempt, some sounds need work.',
                                    mismatch: 'Significant differences from the target phrase.',
                                }[singleResult.match_quality] || 'Analysis complete.'}</FeedbackBox>
                            )}


                            <div className="grid md:grid-cols-2 gap-4 mb-6">
                                <div className="order-3 md:order-1 p-4 bg-stone-50 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs text-stone-500 flex items-center gap-1">
                                            <Target className="h-3 w-3 text-emerald-500" /> Target
                                        </p>
                                        <Button data-testid="single-play-target-btn" variant="ghost" size="sm" className="h-7 px-2" onClick={handlePlayTarget}>
                                            <Volume2 className="h-3 w-3 mr-1" />
                                            {isPlayingTarget ? 'Stop' : 'Play'}
                                        </Button>
                                    </div>
                                    {/* Plain here. The colours belong on Your voice
                                        below: it is their pronunciation being
                                        scored, and it puts the words within reach
                                        of the tip that explains them. */}
                                    <p className="text-sm text-foreground font-medium leading-relaxed">
                                        {currentSinglePhrase?.french || ''}
                                    </p>
                                </div>
                                <div className="order-1 md:order-2 p-4 bg-stone-50 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs text-stone-500 flex items-center gap-1">
                                            <Mic className="h-3 w-3 text-accent" /> Your voice
                                        </p>
                                        {singleRecordingUrl && (
                                            <Button data-testid="single-play-recording-btn" variant="ghost" size="sm" className="h-7 px-2" onClick={handlePlaySingleRecording}>
                                                <PlayCircle className="h-3 w-3 mr-1" />
                                                {isPlayingSingle ? 'Stop' : 'Play'}
                                            </Button>
                                        )}
                                    </div>
                                    {/* The scored words, here rather than on Target.
                                        The words are the target's, because that is
                                        what each score is measured against, but this
                                        is the panel about their attempt and it sits
                                        next to the tips. Same as Manual. */}
                                    <ScoredPhrase phrase={currentSinglePhrase?.french || ''}
                                                  wordScores={singleResult.word_scores || []}
                                                  wordPickable
                                                  scoreLocked={!!singleResult.score_locked}
                                                  phoneTips={singleResult.phone_tips || []}
                                                  stressTips={singleResult.stress_tips || []}
                                                  issues={singleResult.issues || []}
                                                  footerTarget={wordFooter}
                                                  missedWords={singleResult.missed_words || []}
                                                  missedAtEnd={!!singleResult.missed_at_end}
                                                  missedAtStart={!!singleResult.missed_at_start}
                                                  missedAdvice="speed"
                                                  missedDetail={singleResult.missed_detail || []}
                                                  recordedAudio={singleRecordingBlob || singleRecordingUrl} />
                                </div>
                                {/* The picked-word controls land here, via a portal from ScoredPhrase.
                                    Inside the panel they pushed the "Your voice" phrase 78px down a phone.
                                    On a phone the order is Your voice, these controls, then Target, so the
                                    highlighted words and their tips are on screen together. */}
                                <div ref={setWordFooter} className="order-2 md:order-3 md:col-span-2 empty:hidden" />
                            </div>

                            {/* Beta: no separate list. Tapping a highlighted word in the
                                phrase above shows its card, one feature not two. */}
                            {!pronunciationBeta() && (
                            <div className="mb-8">
                                <PronunciationTips
                                    phoneTips={singleResult.phone_tips || []}
                                    stressTips={singleResult.stress_tips || []}
                                    issues={singleResult.issues || []}
                                    scoreLocked={!!singleResult.score_locked}
                                    phrase={currentSinglePhrase?.french || ''}
                                    wordScores={singleResult.word_scores || []}
                                    recordedAudio={singleRecordingBlob || singleRecordingUrl}
                                />
                            </div>
                            )}
                        </CardContent>
                    </Card>
                    <div className="flex flex-wrap gap-4 justify-center mt-8">
                        <Button data-testid="single-retry-btn" variant="outline" className="rounded-full border-stone-300 px-6" onClick={handleSingleRetry}>
                            <Repeat className="h-4 w-4 mr-1" />
                            Retry
                        </Button>
                        <Button data-testid="single-back-btn" className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground text-xs shadow-md px-5" onClick={() => navigate(returnTo)}>
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back to {returnTo === '/collections' ? 'Phrases' : 'Dashboard'}
                        </Button>
                    </div>
                    </>
                ) : (
                <>
                {/* Progress */}
                <div className="mb-6">
                    <div className="flex justify-between text-sm text-primary-foreground/70 mb-2">
                        <span>
                            {isActive ? `Phrase ${currentIndex + 1} of ${phrases.length}` 
                            : isPaused ? `Paused at phrase ${currentIndex + 1} of ${phrases.length}`
                            : `${phrases.length} phrases ready`}
                        </span>
                        {isRecordMode && sessionStats.practiced > 0 && (
                            <span className="font-medium">Avg: {Math.round(sessionStats.totalScore / sessionStats.practiced)}%</span>
                        )}
                        <span>{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" data-testid="session-progress" />
                </div>

                {/* Current Phrase Card */}
                <PhraseCard data-testid="session-phrase-card" {...advanceSwipe}>
                        {(isActive || isPaused) && currentPhrase ? (
                            <div className="text-center">
                                {/* Exit — plain red X, hard against the corner so it never
                                    sits over the phrase text. No background chip. */}
                                <button
                                    type="button"
                                    data-testid="session-exit-x-btn"
                                    onClick={handleBackClick}
                                    aria-label="Exit session"
                                    title="Exit"
                                    className="absolute top-0 right-0 z-10 inline-flex items-center justify-center w-8 h-8 text-stone-400 hover:text-stone-600"
                                >
                                    <X className="h-5 w-5" strokeWidth={2.5} />
                                </button>
                                {/* Phrase — English on top, French below, phonetics on demand.
                                    Word tints from Speechace are painted onto THIS phrase
                                    once a score comes back (or while paused reading), so we
                                    don't repeat the phrase in a second coloured block. */}
                                {/* The score, its label and what it means sit ABOVE the
                                    phrase, the same order as Manual (Leanne, 19 Oct 2026). */}
                                {(phase === 'score' || isPaused) && lastScore && (<>
                                    <ScoreHeader score={lastScore.score} locked={!!lastScore.scoreLocked}
                                                 onUpgrade={() => setUpgradePrompt(lastScore.lockedMessage)}
                                                 className="mb-4" />
                                    {/* A recording problem straight under the score, above the
                                        timer and the phrase (Leanne, 27 Oct 2026). */}
                                    {(() => { const n = scoreFlashNotice(lastScore); return n && lastScore.audioIssue && (
                                        <p className={`text-sm max-w-sm mx-auto text-center mb-4 ${n.className}`}>{n.text}</p>
                                    ); })()}
                                    {/* Pause and the timeline straight under the score, above
                                        the Feedback box, so both stay on a phone screen. */}
                                    <div className="flex flex-col items-center gap-3 mb-4" data-testid="score-controls">
                                        {sessionControls}
                                    </div>
                                    {/* Order, same as Manual (Leanne, 21 Oct 2026): score,
                                        Feedback box, "Tap any word", then the phrase. A
                                        recording problem keeps its own notice. */}
                                    {(() => { const n = scoreFlashNotice(lastScore); return n && !lastScore.audioIssue && (
                                        <div className="w-full max-w-sm mx-auto"><FeedbackBox className="mb-3">{n.text}</FeedbackBox></div>
                                    ); })()}
                                    {pronunciationBeta() && !lastScore.scoreLocked && <TapWordPrompt />}
                                </>)}
                                <PhraseDisplay
                                    phrase={currentPhrase}
                                    activeCharIndex={activeCharIndex}
                                    karaokeEnabled={karaokeEnabled}
                                    englishSpeaking={englishSpeaking}
                                    wordScores={(phase === 'score' || isPaused) ? (lastScore?.wordScores || null) : null}
                                    tipKeys={(phase === 'score' || isPaused) ? feedbackKeys({
                                        phoneTips: lastScore?.phoneTips,
                                        stressTips: lastScore?.stressTips,
                                        issues: lastScore?.issues,
                                        wordScores: lastScore?.wordScores,
                                        missed: pronunciationBeta() ? (lastScore?.missedDetail || []) : [],
                                    }) : null}
                                    // Beta: tap a highlighted word for its card. Tapping
                                    // pauses the run so the next phrase does not take the
                                    // card away mid-read; Resume carries on as normal.
                                    onPickWord={(pronunciationBeta() && (phase === 'score' || isPaused)
                                                 && lastScore && !lastScore.scoreLocked)
                                        ? (w) => { setPickedWord(w); if (!isPaused) handlePause(); }
                                        : null}
                                    pickedKey={pickedWord ? occKey(pickedWord.word, pickedWord.occ) : null}
                                    frenchTestId="session-french-text"
                                    englishTestId="session-english-text"
                                    belowPhrase={scoreTips}
                                />

                                {/* Pause / Resume under the phrase while it runs (below the
                                    phonetics toggle, so it never overlaps the phrase on narrow
                                    screens). On the score it sits under the score instead. */}
                                {!scoreShowing && (
                                    <div className="mt-3">{sessionControls}</div>
                                )}

                                {/* Phase indicators */}
                                <div className="mt-6 min-h-[60px] flex flex-col items-center justify-center">
                                    {isPaused ? (
                                        <div className="flex flex-col items-center gap-3 w-full" data-testid="phase-paused">
                                            {/* Paused indicator sits at the TOP — same slot the score
                                                progress bar occupies in the running score view — so it's
                                                obvious under the Resume button without having to scroll. */}
                                            {/* Tapping it resumes too (Leanne, 4 Nov 2026). */}
                                            <button type="button" onClick={handleResume} data-testid="paused-resume-btn"
                                                    aria-label="Resume session"
                                                    className="flex items-center gap-2">
                                                <Pause className="h-3.5 w-3.5 text-primary" />
                                                <span className="text-xs text-primary font-semibold">Paused</span>
                                            </button>
                                        </div>
                                    ) : (<>
                                    {phase === 'playing' && (
                                        <div className="flex justify-center gap-1" data-testid="phase-playing">
                                            {[1,2,3,4,5].map(i => (
                                                <div key={i} className="w-1.5 bg-primary rounded-full animate-pulse" style={{height: `${12 + Math.random()*20}px`, animationDelay: `${i*0.1}s`}}></div>
                                            ))}
                                        </div>
                                    )}
                                    {phase === 'countdown' && countdownValue !== null && (
                                        <div className="flex flex-col items-center gap-2" data-testid="phase-countdown">
                                            {countdownValue === 'go' ? (
                                                <span className="text-3xl font-bold text-primary tabular-nums">Go!</span>
                                            ) : (
                                                <>
                                                    <p className="text-sm text-stone-500 font-medium">Get ready to speak...</p>
                                                    <span className="text-5xl font-bold text-primary tabular-nums">{countdownValue}</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {phase === 'gap' && (
                                        <p className="text-sm text-purple-500 font-medium animate-pulse" data-testid="phase-gap">
                                            Your turn, repeat now...
                                        </p>
                                    )}
                                    {phase === 'recording' && (
                                        <div className="flex flex-col items-center gap-2 w-full" data-testid="phase-recording">
                                            <Mic className={`h-7 w-7 animate-pulse ${recordingProgress > 80 ? 'text-amber-500' : 'text-rose-500'}`} />
                                            <p className={`text-sm font-medium ${recordingProgress > 80 ? 'text-amber-600' : 'text-rose-500'}`}>
                                                {recordingProgress > 80 ? 'Wrap up now' : 'Speak now...'}
                                            </p>
                                            <p className="text-[10px] text-stone-400">30-second limit</p>
                                        </div>
                                    )}
                                    {phase === 'playback' && (
                                        <div className="flex flex-col items-center gap-3 w-full" data-testid="phase-playback">
                                            <Play className="h-7 w-7 text-primary animate-pulse" />
                                            <p className="text-sm text-primary font-medium">Playing back your recording...</p>
                                        </div>
                                    )}
                                    {phase === 'analyzing' && (
                                        <div className="flex items-center gap-2" data-testid="phase-analyzing">
                                            <Loader2 className="h-5 w-5 text-primary animate-spin" />
                                            <p className="text-sm text-stone-500">Analyzing pronunciation...</p>
                                        </div>
                                    )}
                                    {phase === 'analysis-error' && (
                                        <p className="text-sm text-rose-500 font-medium text-center px-4" data-testid="phase-analysis-error">
                                            Pronunciation analysis is slow or unavailable right now (blame google!). Try Listen &amp; Repeat mode for a smoother practice without scoring.
                                        </p>
                                    )}
                                    </>)}
                                </div>
                            </div>
                        ) : sessionSummary ? (
                            <div className="py-2" data-testid="session-summary">
                                <SessionSummary results={sessionSummary} challengeDay={user?.challenge_day} challengeUserName={user?.name} />
                            </div>
                        ) : statsSummary ? (
                            <div className="py-2 text-center" data-testid="session-stats-summary">
                                <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-accent/15">
                                    <ModeIcon className="h-10 w-10 text-accent" />
                                </div>
                                <h2 className="text-xl font-semibold text-foreground mb-1">Session Complete!</h2>
                                <p className="text-sm text-stone-500 mb-6">
                                    {statsSummary.mode === 'listen-repeat' ? 'Listen & Repeat' : 'Listen Only'}
                                </p>
                                <div className="flex justify-center gap-8 max-w-sm mx-auto">
                                    <div>
                                        <p className="text-3xl font-bold text-foreground">{statsSummary.count}</p>
                                        <p className="text-xs text-stone-500 mt-1">
                                            {statsSummary.count === 1 ? 'phrase' : 'phrases'} {statsSummary.mode === 'listen-only' ? 'listened' : 'practiced'}
                                        </p>
                                    </div>
                                    {statsSummary.durationSecs > 0 && (
                                        <div>
                                            <p className="text-3xl font-bold text-foreground">
                                                {statsSummary.durationSecs >= 60
                                                    ? `${Math.floor(statsSummary.durationSecs / 60)}m ${statsSummary.durationSecs % 60}s`
                                                    : `${statsSummary.durationSecs}s`}
                                            </p>
                                            <p className="text-xs text-stone-500 mt-1">time spent</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-accent/15">
                                    <ModeIcon className="h-8 w-8 text-accent" />
                                </div>
                                <h2 className="text-xl font-semibold text-foreground mb-2">{modeLabel}</h2>
                                <p className="text-stone-500 text-sm max-w-sm mx-auto">
                                    {isRecordMode && 'Each phrase plays, you repeat, your voice is recorded and scored, all automatically.'}
                                    {isRepeatMode && 'Each phrase plays, then you repeat and are recorded. Your voice plays back automatically, no scoring.'}
                                    {isListenOnly && 'Phrases play back-to-back automatically. Just listen and absorb.'}
                                </p>
                            </div>
                        )}
                        {/* Speed, inside the white card as in Manual (Leanne, 21 Oct
                            2026). Hidden on the summary screens, where it no longer
                            applies. */}
                        {!sessionSummary && !statsSummary && (
                            <SpeedControl value={playbackSpeed} onChange={handleSpeedChange}
                                          disabled={isActive} className="mt-6" testId="session-speed-control" />
                        )}
                </PhraseCard>

                {/* Controls */}
                <div className="flex flex-col items-center gap-4">
                    {!isActive && !isPaused ? (
                        <div className="flex flex-col sm:flex-row items-center gap-3">
                            {sessionSummary && sessionSummary.some(r => r.score != null && r.score < REDO_BELOW) && (
                                <Button
                                    data-testid="session-redo-low-btn"
                                    className="rounded-full px-5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs shadow-md"
                                    onClick={handleRedoLowScores}
                                >
                                    <Repeat className="h-5 w-5 mr-1" />
                                    Redo Low Scores
                                </Button>
                            )}
                            <Button
                                data-testid="session-start-btn"
                                className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground text-xs shadow-md px-5"
                                onClick={handleStart}
                            >
                                <Volume2 className="h-5 w-5 mr-1" />
                                {(sessionSummary || statsSummary) ? 'Restart Session' : 'Start Session'}
                            </Button>
                            {(sessionSummary || statsSummary) && (
                                <Select onValueChange={startCollectionSession}>
                                    <SelectTrigger
                                        data-testid="session-other-collection-select"
                                        className="rounded-full px-5 h-9 border-stone-300 bg-transparent text-foreground text-xs w-auto gap-2"
                                    >
                                        <Folder className="h-5 w-5" />
                                        <SelectValue placeholder="Practice different collection" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white text-foreground">
                                        <SelectItem value="__all__">All Phrases</SelectItem>
                                        <CollectionOptions>
                                            {collections.map(c => (
                                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                            ))}
                                        </CollectionOptions>
                                    </SelectContent>
                                </Select>
                            )}
                            <Button
                                data-testid="session-stop-btn"
                                className="rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs shadow-md px-5"
                                onClick={handleBackClick}
                            >
                                <LogOut className="h-5 w-5 mr-1" />
                                Exit
                            </Button>
                        </div>
                    ) : isPaused ? (
                        // Pause/Resume/Exit now live in the phrase-card corners — no
                        // duplicate row here so the buttons only appear in one place.
                        null
                    ) : (
                        // Active-session controls also live in the phrase-card corners.
                        null
                    )}

                </div>
                </>
                )}
            </main>

            <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Stop this session?</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="leave-cancel-btn">No, keep practicing</AlertDialogCancel>
                        <AlertDialogAction data-testid="leave-confirm-btn" onClick={confirmLeave}>
                            Yes, go back to {returnTo === '/collections' ? 'Collections' : 'Dashboard'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Free allowance spent. Same dialog as every other paid wall in
                the app, carrying the server's own wording. */}
            <UpgradeDialog
                open={!!upgradePrompt}
                onOpenChange={(open) => !open && setUpgradePrompt(null)}
                title="Pronunciation scoring is a paid feature"
                message={upgradePrompt}
                showOpinionBox
            />
        </div>
    );
};

export default Automatic;
